import addDays from 'date-fns/addDays';
import startOfDay from 'date-fns/startOfDay';

import { axiosInstance, getHeaders } from './axios';
import { getDateCheckInOutISOString } from './dates';
import { Urls } from '@share/constants';

import {
  CertificateStatusEnum,
  CondoLocationsEnum,
  ICondoLocation,
  ILocation,
  ICertificateItem,
  ItemTypeEnum,
  IUserCertificate,
  IAccount,
  INavigation,
} from '@share/common-types';
import { isPublicClubSiteEnabled } from './public-clubsite';
export const CERTIFICATE_BOOTSTRAP_CITY = 'Florida';
export const CERTIFICATE_BOOTSTRAP_CONDO_LOCATION_QUERY = `${CERTIFICATE_BOOTSTRAP_CITY}, United States of America`;

const minNights = 1;

const isActiveCertificateItemByType = (item: ICertificateItem, itemType: ItemTypeEnum): boolean => {
  return item.type === itemType && item.status === CertificateStatusEnum.Active;
};

export function filterCertificateItemsByTypeAndStatus(
  items: ICertificateItem[] | undefined,
  itemType: ItemTypeEnum,
): ICertificateItem[] {
  if (!items?.length) {
    return [];
  }
  return items.filter((item) => isActiveCertificateItemByType(item, itemType));
}

export function filterUserCertificatesByTypeAndStatus(
  certificates: IUserCertificate[] | undefined,
  itemType: ItemTypeEnum,
): IUserCertificate[] {
  if (!certificates?.length) {
    return [];
  }
  return certificates.filter((certificate) => {
    return filterCertificateItemsByTypeAndStatus(certificate.items, itemType).length > 0;
  });
}

export function findUserCertificateById(
  certificates: IUserCertificate[] | undefined,
  certificateId: string,
): IUserCertificate | undefined {
  const trimmed = certificateId?.trim();
  if (!trimmed || !certificates?.length) {
    return undefined;
  }
  return certificates.find((c) => c.id === trimmed);
}

export function findCertificateItemIdForItemType(
  certificate: IUserCertificate | undefined,
  itemType: ItemTypeEnum,
): string | undefined {
  return filterCertificateItemsByTypeAndStatus(certificate?.items, itemType)?.[0]?.id;
}

/** Sum of nights on items matching `itemType`; defaults to `minNights` if none match. */
export function getTotalNightsForCertificateItemType(
  certificate: IUserCertificate | undefined,
  itemType: ItemTypeEnum,
): number {
  const items = filterCertificateItemsByTypeAndStatus(certificate?.items, itemType);
  const total = items.reduce((sum, i) => sum + (Number(i.nights) || 0), 0);
  return total > 0 ? total : minNights;
}

/**
 * Check-in = start of today (local), check-out = after `nights` nights.
 */
export function getCheckInOutRangeFromTodayNights(nights: number): {
  startDate: string;
  endDate: string;
} {
  const n = Math.max(minNights, Math.floor(nights));
  const start = startOfDay(new Date());
  const end = addDays(start, n);
  return {
    startDate: getDateCheckInOutISOString(start),
    endDate: getDateCheckInOutISOString(end),
  };
}

export async function fetchCondoLocationForCertificateBootstrap(): Promise<ICondoLocation | null> {
  try {
    const res = await axiosInstance.get(Urls.CondosLocations, {
      ...getHeaders(),
      params: { SearchString: CERTIFICATE_BOOTSTRAP_CONDO_LOCATION_QUERY.trim() },
    });
    const locations: ICondoLocation[] = res.data?.locations ?? [];
    const preferred =
      locations.find(
        (l) =>
          l.locationType === CondoLocationsEnum.GenericRegion &&
          (l.name?.includes(CERTIFICATE_BOOTSTRAP_CITY) ?? false),
      ) ??
      locations.find((l) => l.locationType === CondoLocationsEnum.GenericRegion) ??
      locations[0];
    if (!preferred?.id) {
      return null;
    }
    return {
      id: preferred.id,
      name: preferred.name,
      total: preferred.total,
      locationType: preferred.locationType,
      geoLocation: preferred.geoLocation,
    };
  } catch (e) {
    console.error(e);
    return null;
  }
}

/** Resolves hotel search location via locations search query. */
export async function fetchHotelLocationForCertificateBootstrap(): Promise<ILocation | null> {
  try {
    const response = await axiosInstance.get(Urls.Locations, {
      ...getHeaders(),
      params: {
        SearchString: CERTIFICATE_BOOTSTRAP_CONDO_LOCATION_QUERY.trim(),
      },
    });
    const locations = response?.data?.locations as ILocation[] | undefined;
    return locations?.[0] ?? null;
  } catch (e) {
    console.error(e);
    return null;
  }
}

/**
 * Resolves certificate visibility for the current navigation org/site (RSI template + allowlists).
 * Called from `getNavigationMenu` after navigation `items` load; result is merged onto `account.displayCertificates`.
 */
export function resolveDisplayCertificatesForNavigation(
  account: IAccount | null | undefined,
  items: INavigation | null | undefined,
): boolean {
  if (isPublicClubSiteEnabled(account)) {
    return true;
  }

  if (!account?.displayCertificates) {
    return false;
  }

  if (!account.isRSITemplate) {
    return true;
  }

  const siteIdList = account.displayCertificatesSiteidList;
  const orgIdList = account.displayCertificatesOrgidList;
  if (!siteIdList?.length && !orgIdList?.length) {
    return true;
  }

  const siteId = items?.siteId?.toString() ?? '';
  const orgId = items?.organizationId?.toString() ?? '';
  return !!siteIdList?.includes(siteId) || !!orgIdList?.includes(orgId);
}

/** Use after navigation menu has merged resolved `displayCertificates` onto the account (see `getNavigationMenu`). */
export function isDisplayCertificates(account: IAccount | null | undefined): boolean {
  return !!account?.displayCertificates;
}

/** Certificate is actively applied for pricing (display enabled and a certificate id selected). */
export function isCertificateApplied(
  account: IAccount | null | undefined,
  certificateId: string | null | undefined,
): boolean {
  return isDisplayCertificates(account) && !!certificateId?.trim();
}
